/**
 * peeku-v2.js — Peek Ü theme v2 (Proximity corrections)
 *
 * Cross-sell block of the "added to cart" modal ("With this product also buy").
 * Cards are rendered by templates/collection.ajax_related.liquid (loaded via AJAX
 * by vendor.min.js -> relatedCartProducts) and expose their variants as JSON in
 * <script class="pk-rel-variants">. This file provides:
 *   - the card styles (injected once, theme CSS has no rules for them)
 *   - color swatch / size button selection with per-color availability
 *   - AJAX add to cart (fetch /cart/add.js) refreshing the header counter and
 *     the modal totals through the theme's own addToCartHandler / addedModal
 *     helpers (assets/vendor.min.js) WITHOUT closing the modal.
 *
 * NOTE: card markup must NOT use the class `tt-img` on the image wrapper —
 * addedModal.initSingleItem (vendor.min.js) runs
 * $('#modalAddToCartProduct').find('.tt-img').empty() and would wipe every
 * card image. The wrapper class is `pk-rel-imgwrap`. Other addedModal /
 * addToCartHandler selectors (.tt-title-js, .description, .tt-qty, .tt-price,
 * .modal-total-quantity, .full-total-js) are not used by the cards either.
 *
 * Loaded with `defer` from snippets/modal-addtocart.liquid (present on all pages).
 * All event handlers are delegated because the cards are injected (and cloned
 * by the slick slider) after page load.
 */
(function () {
  'use strict';

  var PRIMARY = '#2879FE';
  var PRIMARY_HOVER = '#246EE6';

  /* ------------------------------------------------------------------ */
  /* Styles (injected once)                                              */
  /* ------------------------------------------------------------------ */
  var CSS = [
    '#modalAddToCartProduct .modal-dialog{max-width:940px;width:95%}',
    '.pk-rel-card{padding:0 6px;text-align:left}',
    '.pk-rel-card .pk-rel-imgwrap{display:block;overflow:hidden;-webkit-transition:opacity .3s ease-out;transition:opacity .3s ease-out}',
    '.pk-rel-card .pk-rel-imgwrap img{width:100%;height:auto;display:block}',
    // Le coeur des favoris n'etait pas centre dans sa pastille : le glyphe
    // se collait en haut a gauche, decale de 7,5 px en horizontal et 6,5 px
    // en vertical. La cause n'est pas l'absence de centrage — il est bien
    // declare ci-dessous — mais une regle du theme qui l'annule :
    //   #modalAddToCartProduct .tt-modal-slider .header-menu-product a
    //     { display: block }
    // de specificite 121, la ou « .pk-rel-wishlist » ne vaut que 10. Sans
    // display:flex, align-items et justify-content n'ont aucun effet.
    // On repond par un selecteur de specificite 131, assez precis pour ne
    // toucher que cette pastille, plutot que par un !important qui masquerait
    // le conflit sans le documenter.
    '.pk-rel-wishlist{position:absolute;top:7px;right:8px;z-index:4;width:30px;height:30px;display:flex;align-items:center;justify-content:center;background:#fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.18);transition:opacity .2s}',
    '#modalAddToCartProduct .pk-rel-card .pk-rel-imgbox a.pk-rel-wishlist{display:flex;align-items:center;justify-content:center;padding:0;line-height:0;text-align:center}',
    '#modalAddToCartProduct .pk-rel-card .pk-rel-imgbox a.pk-rel-wishlist svg{display:block;margin:0}',
    '.pk-rel-wishlist svg{width:15px;height:15px}',
    '.pk-rel-info{padding:8px 2px 4px}',
    '.pk-rel-vendor{font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:' + PRIMARY + '}',
    '.pk-rel-cat{font-size:10px;letter-spacing:.05em;text-transform:uppercase;color:#999;margin-top:1px}',
    // Titre de carte : 13 px, deux lignes au plus, le reste coupe. Sans cela
    // « TERRA - Boys' Seasonal Zip-off Pant » occupait cinq lignes et 199 px de
    // haut dans une carte large de 156 px.
    '.pk-rel-title{font-size:13px;font-weight:600;line-height:1.3;margin:5px 0 4px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
    '.pk-rel-title a{font-size:inherit;line-height:inherit;font-weight:inherit}',
    '.pk-rel-title a{color:inherit}',
    '.pk-rel-price{font-size:13px;margin-bottom:7px}',
    '.pk-rel-price .pk-price-new{color:' + PRIMARY + ';font-weight:700}',
    '.pk-rel-price .pk-price-old{color:#999;font-weight:400;text-decoration:line-through;margin-left:6px}',
    '.pk-rel-colors,.pk-rel-sizes{list-style:none;margin:0 0 7px;padding:0;display:flex;flex-wrap:wrap;gap:5px}',
    // La pastille porte la photo de la variante. Le fond passe de #f1f1f1 a
    // blanc : le gris etait precisement ce qui se lisait comme un defaut.
    // 24 px et non 18 : a 18 px une photo recadree reste illisible, la mesure
    // situe le seuil de lecture nette entre 24 et 26 px.
    '.pk-rel-color{display:block;width:24px;height:24px;border-radius:50%;border:1px solid #ddd;padding:0;cursor:pointer;background-color:#fff;background-repeat:no-repeat;background-position:50% 42%;background-size:300%}',
    // repli quand ni la variante ni le produit n'ont d'image
    '.pk-rel-color[data-sans-image]{width:auto;min-width:24px;padding:0 6px;border-radius:12px;font-size:9px;line-height:22px;text-transform:uppercase;color:#666;background-size:auto}',
    '.pk-rel-color.active{box-shadow:0 0 0 2px #fff,0 0 0 4px ' + PRIMARY + ';border-color:' + PRIMARY + '}',
    '.pk-rel-color.pk-soldout{opacity:.3;cursor:not-allowed}',
    '.pk-rel-size{display:block;min-width:30px;padding:2px 9px;border-radius:14px;border:1px solid #ddd;background:#fff;font-size:11px;line-height:1.6;cursor:pointer;transition:background .15s,color .15s}',
    '.pk-rel-size.active{background:' + PRIMARY + ';border-color:' + PRIMARY + ';color:#fff}',
    '.pk-rel-size.pk-soldout{text-decoration:line-through;opacity:.4;cursor:not-allowed}',
    '.pk-rel-add{display:block;width:100%;padding:9px 10px;border:0;border-radius:20px;background:' + PRIMARY + ';color:#fff;font-size:12px;font-weight:600;text-transform:uppercase;cursor:pointer;transition:background .2s}',
    '.pk-rel-add:hover{background:' + PRIMARY_HOVER + '}',
    '.pk-rel-add .icon{margin-right:5px}',
    '.pk-rel-add.pk-disabled{opacity:.5;cursor:not-allowed;pointer-events:none}',
    '.pk-rel-add.pk-loading{opacity:.6;pointer-events:none}',

    /* Indicateur d'attente sur les boutons d'ajout au panier.
       Mesure : 2 431 ms s'ecoulent entre le clic et l'ouverture de la fenetre
       de confirmation, sans le moindre retour visuel — le bouton gardait son
       texte, restait actif et acceptait d'autres clics. La couleur du cercle
       est lue sur le bouton au moment du clic (--pk-spin) : elle reste donc
       lisible sur un fond bleu comme sur un fond clair. */
    '.pk-en-cours,.pk-rel-add.pk-loading{position:relative;color:transparent!important;pointer-events:none;cursor:progress}',
    '.pk-en-cours>*,.pk-rel-add.pk-loading>*{visibility:hidden}',
    '.pk-en-cours::after,.pk-rel-add.pk-loading::after{content:"";position:absolute;top:50%;left:50%;width:18px;height:18px;margin:-9px 0 0 -9px;box-sizing:border-box;border-radius:50%;border:2px solid var(--pk-spin,#fff);border-top-color:transparent;animation:pk-rotation .7s linear infinite}',
    '@keyframes pk-rotation{to{transform:rotate(360deg)}}',
    /* mouvement reduit : on ralentit sans supprimer, l'attente doit rester lisible */
    '@media (prefers-reduced-motion: reduce){.pk-en-cours::after,.pk-rel-add.pk-loading::after{animation-duration:2.4s}}',

    /* Coeur des favoris : bleu des que le produit est dans la liste.
       L'etat est porte par data-action, qui vaut « wk-remove » lorsque le
       produit y figure — verifie : la valeur survit au rechargement, elle est
       reconstruite depuis localStorage.wokiee_wishlist. */
    '.wlbutton-js[data-action="wk-remove"] svg path{fill:' + PRIMARY + '!important}',
    '.wlbutton-js[data-action="wk-remove"] svg{stroke:' + PRIMARY + '}',
    '.wlbutton-js[data-action="wk-remove"]{color:' + PRIMARY + '}'
  ].join('\n');

  if (!document.getElementById('pk-v2-css')) {
    var style = document.createElement('style');
    style.id = 'pk-v2-css';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */
  function closestCard(el) {
    return el.closest ? el.closest('.pk-rel-card') : null;
  }

  function getVariants(card) {
    if (card._pkVariants) return card._pkVariants;
    var variants = [];
    try {
      var node = card.querySelector('.pk-rel-variants');
      if (node) variants = JSON.parse(node.textContent);
    } catch (e) {
      variants = [];
    }
    card._pkVariants = variants;
    return variants;
  }

  function variantOptions(variant) {
    return [variant.option1, variant.option2, variant.option3];
  }

  /* Current selection: { optionIndex: value } for every rendered group. */
  function currentSelection(card) {
    var sel = {};
    card.querySelectorAll('.pk-rel-colors, .pk-rel-sizes').forEach(function (ul) {
      var active = ul.querySelector('.active');
      sel[ul.getAttribute('data-option-index')] = active ? active.getAttribute('data-value') : null;
    });
    return sel;
  }

  function matchVariant(variants, sel) {
    for (var i = 0; i < variants.length; i++) {
      var opts = variantOptions(variants[i]);
      var ok = true;
      for (var idx in sel) {
        if (sel[idx] != null && opts[idx] !== sel[idx]) { ok = false; break; }
      }
      if (ok) return variants[i];
    }
    return null;
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && typeof money_format !== 'undefined') {
      return Shopify.formatMoney(cents, money_format);
    }
    return (cents / 100).toFixed(2);
  }

  /* Recompute size availability for the selected color, fix selection,
     refresh price / image / add-to-cart button state. */
  function refreshCard(card) {
    var variants = getVariants(card);
    var sel = currentSelection(card);

    card.querySelectorAll('.pk-rel-sizes').forEach(function (ul) {
      var idx = ul.getAttribute('data-option-index');
      ul.querySelectorAll('.pk-rel-size').forEach(function (btn) {
        var test = {};
        for (var k in sel) test[k] = sel[k];
        test[idx] = btn.getAttribute('data-value');
        var v = matchVariant(variants, test);
        btn.classList.toggle('pk-soldout', !(v && v.available));
      });
      /* If the active size is not available in this color, move to the
         first available one. */
      var active = ul.querySelector('.pk-rel-size.active');
      if (active && active.classList.contains('pk-soldout')) {
        active.classList.remove('active');
        var firstOk = ul.querySelector('.pk-rel-size:not(.pk-soldout)');
        if (firstOk) firstOk.classList.add('active');
      }
    });

    var variant = matchVariant(variants, currentSelection(card));
    var addBtn = card.querySelector('.pk-rel-add');
    var priceNew = card.querySelector('.pk-price-new');
    var priceOld = card.querySelector('.pk-price-old');

    if (variant) {
      card.setAttribute('data-variant-id', variant.id);
      if (priceNew) priceNew.innerHTML = formatMoney(variant.price);
      if (priceOld) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceOld.innerHTML = formatMoney(variant.compare_at_price);
          priceOld.style.display = '';
        } else {
          priceOld.style.display = 'none';
        }
      }
      if (variant.featured_image && variant.featured_image.src) {
        var img = card.querySelector('.pk-rel-img img');
        if (img && img.getAttribute('src') !== variant.featured_image.src) {
          img.setAttribute('src', variant.featured_image.src);
        }
      }
      if (addBtn) addBtn.classList.toggle('pk-disabled', !variant.available);
    } else if (addBtn) {
      addBtn.classList.add('pk-disabled');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Delegated interactions                                              */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var swatch = e.target.closest ? e.target.closest('.pk-rel-color') : null;
    if (swatch) {
      e.preventDefault();
      if (swatch.classList.contains('pk-soldout')) return;
      var card = closestCard(swatch);
      if (!card) return;
      swatch.closest('.pk-rel-colors').querySelectorAll('.pk-rel-color').forEach(function (el) {
        el.classList.remove('active');
      });
      swatch.classList.add('active');
      refreshCard(card);
      return;
    }

    var size = e.target.closest ? e.target.closest('.pk-rel-size') : null;
    if (size) {
      e.preventDefault();
      if (size.classList.contains('pk-soldout')) return;
      var card2 = closestCard(size);
      if (!card2) return;
      size.closest('.pk-rel-sizes').querySelectorAll('.pk-rel-size').forEach(function (el) {
        el.classList.remove('active');
      });
      size.classList.add('active');
      refreshCard(card2);
      return;
    }

    var addBtn = e.target.closest ? e.target.closest('.pk-rel-add') : null;
    if (addBtn) {
      e.preventDefault();
      addToCart(addBtn);
    }
  });

  /* ------------------------------------------------------------------ */
  /* AJAX add to cart — keeps the modal open                             */
  /* ------------------------------------------------------------------ */
  function addToCart(btn) {
    var card = closestCard(btn);
    if (!card || btn.classList.contains('pk-loading') || btn.classList.contains('pk-disabled')) return;

    var variant = matchVariant(getVariants(card), currentSelection(card));
    if (!variant || !variant.available) return;

    btn.classList.add('pk-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variant.id, quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) { throw data; });
        }
        return res.json();
      })
      .then(function (lineItem) {
        /* Refresh header badge + totals and the modal totals through the
           theme's own helpers (vendor.min.js), without closing the modal. */
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            if (window.addToCartHandler) {
              addToCartHandler.addItem(lineItem);
              addToCartHandler.updateGeneralInfo(cart);
            }
            if (window.addedModal) {
              addedModal.initSingleItem(lineItem);
              addedModal.initGeneralInfo(cart);
            }
            if (window.jQuery) jQuery('body').trigger('refreshCurrency');
          });
      })
      .catch(function (err) {
        /* Same error surface as the theme (Shopify.onError in vendor.min.js). */
        var msg = (err && (err.description || err.message)) || 'Error';
        if (window.jQuery) {
          jQuery('#modalAddToCartError .error_message').text(msg);
          jQuery('#modalAddToCartError').modal('toggle');
        }
      })
      .then(function () {
        btn.classList.remove('pk-loading');
      });
  }
})();

/* ------------------------------------------------------------------
 * Carrousel d'accueil : chargement differe des diapositives suivantes
 *
 * Le carrousel imposait « preload » vide a chaque video, ce qui vaut « auto » :
 * les trois se telechargeaient a l'ouverture, soit 46 Mo sur telephone pour une
 * seule visible. Le reglage a ete ramene a « none ».
 *
 * Effet de bord mesure : la diapositive suivante commencait alors sa lecture
 * avec environ deux secondes de poster fige, le temps du telechargement. On
 * relance donc le chargement des autres videos une fois la page entierement
 * chargee, hors du chemin critique. Le premier affichage garde son gain, la
 * transition retrouve sa fluidite.
 *
 * Respecte « economiseur de donnees » et les connexions lentes : sur 2G ou en
 * mode economie, on ne precharge rien et on laisse la lecture a la demande.
 * ------------------------------------------------------------------ */
(function () {
  var DELAI = 6000; // apres le chargement complet, bien au-dela du plus grand affichage

  function precharger() {
    var c = navigator.connection;
    if (c && (c.saveData || /^(slow-)?2g$/.test(c.effectiveType || ''))) return;

    var videos = document.querySelectorAll('.slider-revolution video, .tp-banner video');
    Array.prototype.forEach.call(videos, function (v) {
      if (v.readyState > 0) return;              // deja en cours ou chargee
      v.setAttribute('preload', 'auto');
      try { v.load(); } catch (e) { /* rien a faire : la lecture a la demande reste possible */ }
    });
  }

  function armer() { setTimeout(precharger, DELAI); }
  if (document.readyState === 'complete') armer();
  else window.addEventListener('load', armer, { once: true });
})();

/* ------------------------------------------------------------------
 * Pages de catalogue : choisir un coloris change la photo de la carte
 *
 * Le theme possede deja un gestionnaire (assets/theme.min.js), mais au-dessus
 * de 1024 px il met a jour « .tt-img-roll-over img », c'est-a-dire l'image de
 * SURVOL, et laisse la photo principale inchangee. Sur un ordinateur, cliquer
 * un coloris ne se voyait donc pas : la photo affichee restait celle du premier
 * coloris, et la nouvelle n'apparaissait qu'en passant la souris dessus.
 *
 * On complete sans toucher au gestionnaire d'origine : a chaque clic sur une
 * pastille, on pose aussi l'adresse portee par « data-img » sur la photo
 * principale. Le chargement differe est neutralise sur cette image precise,
 * sans quoi le script de chargement la remplacerait par celle d'origine.
 *
 * Delegue sur le document : les cartes sont injectees ou clonees apres le
 * chargement de la page (defilement infini, carrousels).
 * ------------------------------------------------------------------ */
(function () {
  function appliquer(pastille) {
    var li = pastille.closest('li[data-img]');
    var carte = pastille.closest('.options-js');
    if (!li || !carte) return;
    var src = li.getAttribute('data-img');
    if (!src) return;

    var principale = carte.querySelector('.tt-img img');
    if (!principale) return;

    // le chargement differe reecrirait src depuis data-src : on le desarme
    principale.removeAttribute('data-src');
    principale.removeAttribute('data-srcset');
    principale.removeAttribute('srcset');
    principale.classList.remove('lazyload');
    principale.setAttribute('src', src);

    // certaines cartes reservent la hauteur tant que l'image n'est pas peinte
    var reserve = carte.querySelector('[data-mainimage]');
    if (reserve) {
      reserve.removeAttribute('data-mainimage');
      if (reserve.parentElement) reserve.parentElement.style.height = 'auto';
    }
  }

  document.addEventListener('click', function (e) {
    var p = e.target.closest ? e.target.closest('a.options-color') : null;
    if (!p) return;
    // apres le gestionnaire du theme, pour ne pas etre ecrase
    setTimeout(function () { appliquer(p); }, 0);
  }, true);
})();

/* ------------------------------------------------------------------
 * Attente visible sur les boutons d'ajout au panier
 *
 * Mesure avant : 2 431 ms s'ecoulent entre le clic et l'ouverture de la
 * fenetre de confirmation. Pendant ce temps le bouton ne changeait pas —
 * meme texte, toujours actif, acceptant d'autres clics. Le visiteur ne sait
 * pas si sa demande est partie, et un second clic ajoute une seconde fois.
 *
 * On pose donc un cercle qui tourne, retire des que la fenetre s'ouvre. Trois
 * garde-fous, parce qu'un indicateur bloque est pire que pas d'indicateur :
 *   - la fenetre d'erreur le retire aussi ;
 *   - une echeance de douze secondes le retire dans tous les cas ;
 *   - un retour en arriere dans l'historique remet la page a plat.
 * ------------------------------------------------------------------ */
(function () {
  var CIBLES = '.btn-addtocart, button[name="add"], .tt-btn-addtocart, .pk-rel-add';
  var ECHEANCE = 12000;

  function fenetreVisible(id) {
    var m = document.getElementById(id);
    if (!m) return false;
    var s = getComputedStyle(m);
    return s.display !== 'none' && m.getBoundingClientRect().height > 10;
  }

  function arreter(btn) {
    if (!btn) return;
    btn.classList.remove('pk-en-cours');
    btn.removeAttribute('aria-busy');
    btn.style.removeProperty('--pk-spin');
  }

  function demarrer(btn) {
    if (btn.classList.contains('pk-en-cours') || btn.classList.contains('pk-disabled')) return;

    // la couleur du texte AVANT de le rendre transparent : c'est elle qui
    // rendra le cercle lisible, sur un bouton bleu comme sur un bouton clair
    var teinte = getComputedStyle(btn).color;
    if (teinte && teinte !== 'rgba(0, 0, 0, 0)') btn.style.setProperty('--pk-spin', teinte);

    btn.classList.add('pk-en-cours');
    btn.setAttribute('aria-busy', 'true');

    var debut = Date.now();
    var minuteur = setInterval(function () {
      var fini = fenetreVisible('modalAddToCartProduct')
              || fenetreVisible('modalAddToCartError')
              || fenetreVisible('modal_quick_view')
              || Date.now() - debut > ECHEANCE;
      if (!fini) return;
      clearInterval(minuteur);
      arreter(btn);
    }, 80);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest(CIBLES) : null;
    if (!btn) return;
    // les cartes de vente croisee ont deja leur propre marque : on ne double pas
    if (btn.classList.contains('pk-rel-add')) return;
    demarrer(btn);
  }, true);

  // un retour arriere restitue parfois la page telle quelle, indicateur compris
  window.addEventListener('pageshow', function () {
    var l = document.querySelectorAll('.pk-en-cours');
    for (var i = 0; i < l.length; i++) arreter(l[i]);
  });
})();
